import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';

import { useLocalization } from 'localization';
import { createAdsCampaignOrderStore } from 'stores/createAdsCampaignOrderStore';
import CreateAdsCampaignOrderStep1 from './CreateAdsCampaignOrderStep1';
import CreateAdsCampaignOrderStep2 from './CreateAdsCampaignOrderStep2';
import CreateAdsCampaignOrderStep3 from './CreateAdsCampaignOrderStep3';
import CreateAdsCampaignOrderStep4 from './CreateAdsCampaignOrderStep4';
import CreateAdsCampaignOrderStep5 from './CreateAdsCampaignOrderStep5';
import CreateNewServiceContainer from 'components/v2/container/CreateNewServiceContainer';
import StepsProgress from 'components/v2/progress/StepsProgress';
import { useCreateAdsCampaignOrder } from 'hooks/services/useCreateAdsCampaignOrder';

const CreateAdsCampaignOrder = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { form } = useCreateAdsCampaignOrder();
  const { totalStep, discardFlow, goToStep } = createAdsCampaignOrderStore();
  const currentStep = createAdsCampaignOrderStore(state => state.currentStep);
  const formData = createAdsCampaignOrderStore(state => state.formData);

  const methods = useForm({
    resolver: yupResolver(form.schema),
    defaultValues: {
      radius: 20,
    },
    mode: 'onChange',
    values: formData,
  });

  const stepsComponent: { [key: number]: React.JSX.Element } = {
    1: <CreateAdsCampaignOrderStep1 />,
    2: <CreateAdsCampaignOrderStep2 />,
    3: <CreateAdsCampaignOrderStep3 />,
    4: <CreateAdsCampaignOrderStep4 />,
    5: <CreateAdsCampaignOrderStep5 />,
  };

  const stepsTitle: { [key: number]: string } = {
    1: `1. ${t('orders.pages.new.ads.chooseCase')}`,
    2: `2. ${t('orders.pages.new.stepOne.title')}`,
    3: `3. ${t('orders.pages.new.stepThree.optionAddon')}`,
    4: `4. ${t('common.terms.targeting')}`,
    5: `5. ${t('common.terms.confirmation')}`,
  };

  const handleDiscardCreateOrder = () => {
    discardFlow();
    navigate(-1);
  };

  const handleGoToPreviousStep = () => {
    goToStep(currentStep - 1);
  };

  const stepTitleEl = () => {
    return (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {currentStep > 1 && (
          <Tooltip title={t('orders.pages.new.previousStep')} arrow>
            <IconButton aria-label="back" size="small" onClick={handleGoToPreviousStep}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}

        <Typography
          variant="h5"
          sx={{
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {stepsTitle[currentStep]}
        </Typography>
      </Stack>
    );
  };

  return (
    <CreateNewServiceContainer
      title={stepTitleEl()}
      steps={<StepsProgress currentStep={currentStep} totalStep={totalStep} />}
      onClose={handleDiscardCreateOrder}
    >
      <FormProvider {...methods}>{stepsComponent[currentStep]}</FormProvider>
    </CreateNewServiceContainer>
  );
};

export default CreateAdsCampaignOrder;
